import {
  Avatar,
  Badge,
  Checkbox,
  HStack,
  Progress,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FiArrowLeft,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiUserPlus,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AdminLayout,
  Button,
  Card,
  DataTable,
  SearchInput,
} from '@grcs/components';
import { TableColumn } from '@grcs/components/types';
import { useData } from '@grcs/context/DataContext';
import { StudentProfile } from '@grcs/types/DataTypes';

interface StudentSelectionPageProps {}

export const ClassStudentSelectionEnhanced: React.FC<
  StudentSelectionPageProps
> = () => {
  const { classId, year } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { supabase } = useData();

  // State for student selection
  const [allStudents, setAllStudents] = useState<StudentProfile[]>([]);
  const [currentClassStudents, setCurrentClassStudents] = useState<string[]>(
    [],
  );
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const pageSize = 15;

  // Fetch all students with pagination
  useEffect(() => {
    const fetchStudents = async () => {
      if (!supabase) return;

      try {
        setIsLoading(true);
        const from = (currentPage - 1) * pageSize;
        const to = from + pageSize - 1;

        // Build query
        let query = supabase.from('students').select('*', { count: 'exact' });

        // Add search filter
        if (searchQuery) {
          query = query.or(
            `first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`,
          );
        }

        // Add pagination
        const { data, error, count } = await query
          .order('first_name', { ascending: true })
          .range(from, to);

        if (error) throw error;

        setAllStudents((data as StudentProfile[]) || []);
        setTotalCount(count || 0);

        // Debug logging - check if specific student exists
        const targetStudent = data?.find(
          (s) => s.email === 'bo_zhaobocong@hotmail.com',
        );
        console.log('🔍 Looking for bo_zhaobocong@hotmail.com:', {
          found: !!targetStudent,
          student: targetStudent,
          totalStudents: data?.length || 0,
        });

        // Also search for similar emails in case there's a typo
        const similarEmails =
          data?.filter(
            (s) =>
              s.email?.toLowerCase().includes('zhaobocong') ||
              s.email?.toLowerCase().includes('bo_zhao') ||
              s.email?.toLowerCase().includes('bocong'),
          ) || [];

        if (similarEmails.length > 0) {
          console.log(
            '🔍 Found similar emails:',
            similarEmails.map((s) => s.email),
          );
        }

        // Let's also do a direct database query for this specific email
        try {
          const directQuery = await supabase
            .from('students')
            .select('*')
            .eq('email', 'bo_zhaobocong@hotmail.com');

          console.log('📧 Direct email query result:', {
            found: !!directQuery.data?.length,
            student: directQuery.data?.[0] || null,
            error: directQuery.error,
          });
        } catch (error) {
          console.error('Error in direct query:', error);
        }
      } catch (error) {
        console.error('Error fetching students:', error);
        toast({
          title: 'Error loading students',
          description: 'Failed to load student data. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudents();
  }, [currentPage, searchQuery, supabase, pageSize, toast]);

  // Fetch current class students
  useEffect(() => {
    const fetchClassStudents = async () => {
      if (!supabase || !classId) return;

      try {
        const { data, error } = await supabase
          .from('students')
          .select('student_token')
          .eq('class_token', classId);

        if (error) throw error;

        const studentTokens = data?.map((s) => s.student_token) || [];
        setCurrentClassStudents(studentTokens);
      } catch (error) {
        console.error('Error fetching class students:', error);
      }
    };

    fetchClassStudents();
  }, [supabase, classId]);

  // Filter out students already in the class
  const availableStudents = useMemo(() => {
    return allStudents.filter(
      (student) => !currentClassStudents.includes(student.student_token),
    );
  }, [allStudents, currentClassStudents]);

  // Handle adding selected students to class
  const handleAddStudents = async () => {
    if (selectedStudents.length === 0) {
      toast({
        title: 'No students selected',
        description: 'Please select at least one student to add to the class.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsAdding(true);

      // Update selected students' class_token
      const { error } = await supabase
        .from('students')
        .update({ class_token: classId })
        .in('student_token', selectedStudents);

      if (error) throw error;

      toast({
        title: 'Students added successfully',
        description: `Added ${selectedStudents.length} student${
          selectedStudents.length > 1 ? 's' : ''
        } to the class.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Navigate back to class students page
      navigate(`/teacher/${year}/class/${classId}/student`);
    } catch (error) {
      console.error('Error adding students:', error);
      toast({
        title: 'Error adding students',
        description: 'Failed to add students to the class. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAdding(false);
    }
  };

  // Table columns
  const columns: TableColumn<StudentProfile>[] = [
    {
      key: 'selection',
      header: (
        <Checkbox
          isChecked={
            selectedStudents.length === availableStudents.length &&
            availableStudents.length > 0
          }
          isIndeterminate={
            selectedStudents.length > 0 &&
            selectedStudents.length < availableStudents.length
          }
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedStudents(
                availableStudents.map((s) => s.student_token),
              );
            } else {
              setSelectedStudents([]);
            }
          }}
        />
      ),
      width: '50px',
      render: (student) => (
        <Checkbox
          isChecked={selectedStudents.includes(student.student_token)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedStudents((prev) => [...prev, student.student_token]);
            } else {
              setSelectedStudents((prev) =>
                prev.filter((id) => id !== student.student_token),
              );
            }
          }}
        />
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (student) => (
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={`${student.first_name} ${student.last_name}`}
          />
          <VStack align="start" spacing={0}>
            <Text fontWeight="medium">
              {student.first_name} {student.last_name}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {student.email}
            </Text>
          </VStack>
        </HStack>
      ),
    },
    {
      key: 'grade',
      header: 'Grade',
      render: (student) => (
        <Badge colorScheme="blue" variant="subtle">
          Student
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (student) => (
        <Badge colorScheme={student.deleted ? 'red' : 'green'} variant="subtle">
          {student.deleted ? 'Inactive' : 'Active'}
        </Badge>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <Button
              leftIcon={<FiArrowLeft />}
              variant="ghost"
              onClick={() =>
                navigate(`/teacher/${year}/class/${classId}/student`)
              }
            >
              Back to Class
            </Button>
            <VStack align="start" spacing={1}>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                Select Students to Add
              </Text>
              <Text color="gray.600">
                Choose students from the database to add to this class
              </Text>
            </VStack>
          </HStack>

          <Button
            leftIcon={<FiCheck />}
            colorScheme="blue"
            onClick={handleAddStudents}
            isLoading={isAdding}
            loadingText="Adding..."
            isDisabled={selectedStudents.length === 0}
          >
            Add {selectedStudents.length} Student
            {selectedStudents.length !== 1 ? 's' : ''}
          </Button>
        </HStack>

        {/* Search and Filters */}
        <Card>
          <HStack spacing={4} justify="space-between">
            <SearchInput
              placeholder="Search students by name or email..."
              value={searchQuery}
              onChange={setSearchQuery}
            />

            <HStack spacing={2} color="gray.600" fontSize="sm">
              <Text>
                {totalCount > 0 ? (
                  <>
                    Showing{' '}
                    {Math.min((currentPage - 1) * pageSize + 1, totalCount)}-
                    {Math.min(currentPage * pageSize, totalCount)} of{' '}
                    {totalCount} students
                  </>
                ) : (
                  'No students found'
                )}
              </Text>
            </HStack>
          </HStack>
        </Card>

        {/* Student Table */}
        <Card>
          {isLoading ? (
            <VStack spacing={4} py={8}>
              <Progress
                size="xs"
                isIndeterminate
                colorScheme="blue"
                w="200px"
              />
              <Text color="gray.500">Loading students...</Text>
            </VStack>
          ) : (
            <DataTable
              data={availableStudents}
              columns={columns}
              emptyState={
                <VStack spacing={4} py={8}>
                  <FiUserPlus size={48} opacity={0.4} />
                  <VStack>
                    <Text fontSize="lg" fontWeight="600">
                      {searchQuery
                        ? 'No matching students found'
                        : 'No students available'}
                    </Text>
                    <Text color="gray.500">
                      {searchQuery
                        ? 'Try adjusting your search criteria'
                        : 'All students are already assigned to classes'}
                    </Text>
                  </VStack>
                </VStack>
              }
            />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <HStack
              justify="center"
              spacing={1}
              mt={6}
              pt={4}
              borderTop="1px solid"
              borderColor="gray.200"
            >
              <Button
                leftIcon={<FiChevronLeft />}
                size="sm"
                variant="ghost"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Previous
              </Button>

              {/* Page numbers */}
              {(() => {
                const pages = [];
                const showPages = 5;
                const startPage = Math.max(
                  1,
                  currentPage - Math.floor(showPages / 2),
                );
                const endPage = Math.min(totalPages, startPage + showPages - 1);

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <Button
                      key={i}
                      size="sm"
                      variant={
                        i === currentPage ? ('solid' as any) : ('ghost' as any)
                      }
                      colorScheme={i === currentPage ? 'blue' : 'gray'}
                      onClick={() => setCurrentPage(i)}
                      minW="32px"
                    >
                      {i}
                    </Button>,
                  );
                }

                return pages;
              })()}

              <Button
                rightIcon={<FiChevronRight />}
                size="sm"
                variant="ghost"
                isDisabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </Button>
            </HStack>
          )}
        </Card>
      </VStack>
    </AdminLayout>
  );
};
